<template>
  <canvas ref="canvasRef" :style="canvasStyle" />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import type { Rive, ViewModelInstance } from "@rive-app/webgl2";

const props = withDefaults(
  defineProps<{
    src: string;
    stateMachine?: string;
    /**
     * Boolean view model property pulsed on every loop. Empty means the artboard
     * just plays on its own — a looping loader has nothing to toggle.
     */
    property?: string;
    /** Pause between pulses, ms. */
    interval?: number;
    /** How long the property stays raised, ms — cover the animation's length. */
    hold?: number;
    /** Wait for the canvas to approach the viewport before fetching anything. */
    lazy?: boolean;
    width?: number;
    height?: number;
  }>(),
  {
    stateMachine: "State Machine 1",
    property: "",
    interval: 10000,
    hold: 4000,
    lazy: true,
    width: 400,
    height: 1120,
  },
);

/**
 * The CSS size must be pinned: `resizeDrawingSurfaceToCanvas()` scales the width and
 * height *attributes* by the device pixel ratio, and a canvas with no CSS size lays
 * out from those attributes — so on a retina screen it would render at 2× and keep
 * growing on every resize.
 */
const canvasStyle = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
  display: "block",
}));

const canvasRef = ref<HTMLCanvasElement | null>(null);
const rive = shallowRef<Rive | null>(null);
const viewModel = shallowRef<ViewModelInstance | null>(null);

let observer: IntersectionObserver | null = null;
let resizeObserver: ResizeObserver | null = null;
let loopTimer: ReturnType<typeof setTimeout> | null = null;
let holdTimer: ReturnType<typeof setTimeout> | null = null;

function write(value: boolean) {
  const property = viewModel.value?.boolean(props.property);
  if (property) property.value = value;
}

/** Raise the property, drop it after `hold`, then wait `interval` and repeat. */
function loop() {
  write(true);
  holdTimer = setTimeout(() => {
    write(false);
    loopTimer = setTimeout(loop, props.interval);
  }, props.hold);
}

async function start() {
  if (!canvasRef.value || rive.value) return;

  // webgl2, not the lighter canvas build: blur only exists in the Rive Renderer.
  // See AiraFloatingWidget for the full note.
  const { Rive: RiveCtor, Layout, Fit, Alignment } = await import("@rive-app/webgl2");

  rive.value = new RiveCtor({
    src: props.src,
    canvas: canvasRef.value,
    autoplay: true,
    // Naming the machine is required: without it no instance is created and the
    // transition conditions are never evaluated.
    stateMachines: props.stateMachine,
    autoBind: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    onLoad: () => {
      rive.value?.resizeDrawingSurfaceToCanvas();
      viewModel.value = rive.value?.viewModelInstance ?? null;
      if (props.property) loopTimer = setTimeout(loop, props.interval);
    },
  });

  resizeObserver = new ResizeObserver(() => {
    rive.value?.resizeDrawingSurfaceToCanvas();
  });
  resizeObserver.observe(canvasRef.value);
}

onMounted(() => {
  if (!canvasRef.value) return;

  // An overlay has to be on screen the moment it mounts — there is nothing to wait for.
  if (!props.lazy) {
    void start();
    return;
  }

  // The file is heavy and the block sits below the fold, so both the runtime and
  // the .riv are fetched only once the figure is actually approaching the viewport.
  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer?.disconnect();
      observer = null;
      void start();
    },
    { rootMargin: "200px" },
  );
  observer.observe(canvasRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  resizeObserver?.disconnect();
  if (loopTimer) clearTimeout(loopTimer);
  if (holdTimer) clearTimeout(holdTimer);
  observer = null;
  resizeObserver = null;
  loopTimer = null;
  holdTimer = null;
  viewModel.value = null;
  rive.value?.cleanup();
  rive.value = null;
});
</script>
